import type { HouseholdSize } from '@hc-frontend/core-types';
import {
  Applicant,
  Child,
  Gender,
  MemberType,
  Prospect,
  Spouse,
  TobaccoUse,
} from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { getSafeURLParameters } from '@hc-frontend/core-utils-dom';
import { removeEmptyKeys } from '@hc-frontend/core-utils-object';
import { decryptParamsService } from '@hc-frontend/services';

import type { HandleContextualTrafficGetParametersResult } from '../../domain/repositories/handle-contextual-traffic-repository';
import { getParameterValueByKeySet } from './get-parameter-value-by-key-set';

function getDataParam(url: string) {
  const params = url.split('?')[1];

  if (params) {
    const dataParam = params
      .split(/[?&]/g)
      .find((param) => param.includes('data'));

    if (dataParam) {
      return decodeURIComponent(dataParam.split('=')[1]);
    }
  }
  return undefined;
}

function transformGender(value?: string) {
  const lowerCaseValue = value?.trim().toLowerCase();

  if (lowerCaseValue === 'm' || lowerCaseValue === 'male') {
    return Gender.Enum.MALE;
  }

  if (lowerCaseValue === 'f' || lowerCaseValue === 'female') {
    return Gender.Enum.FEMALE;
  }

  return undefined;
}

function transformDob(value?: string) {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date;
}

function transformTobacco(value?: string) {
  const lowerCaseValue = value?.trim().toLowerCase();

  if (lowerCaseValue === 'y') {
    return TobaccoUse.Enum.YES;
  }
  if (lowerCaseValue === 'n') {
    return TobaccoUse.Enum.NO;
  }

  return undefined;
}

function transformHouseholdSize(value?: string) {
  if (!value) {
    return undefined;
  }

  const number = parseInt(value, 10);

  return Number.isNaN(number) ? undefined : number;
}

function transformHouseholdIncome(value?: string) {
  if (!value) {
    return undefined;
  }

  const number = parseFloat(value);

  return Number.isNaN(number) ? undefined : number;
}

function getMember(
  parameters: Record<string, string>,
  suffix: string,
  memberType: MemberType,
) {
  const profile = {
    firstName: getParameterValueByKeySet(parameters, [`fname_${suffix}`]),
    lastName: getParameterValueByKeySet(parameters, [`lname_${suffix}`]),
    gender: transformGender(
      getParameterValueByKeySet(parameters, [`gender_${suffix}`]),
    ),
    dateOfBirth: transformDob(
      getParameterValueByKeySet(parameters, [`dob_${suffix}`]),
    ),
    tobaccoUse: transformTobacco(
      getParameterValueByKeySet(parameters, [`tobacco_${suffix}`]),
    ),
    memberType,
  };

  return profile.dateOfBirth && profile.gender
    ? removeEmptyKeys(profile)
    : undefined;
}

function getApplicant(urlParameters: Record<string, string>) {
  const tobaccoUse = transformTobacco(
    getParameterValueByKeySet(urlParameters, [
      'tobacco',
      'tobaccouse',
      'applicant[applicant][tobacco]',
      'tb1',
    ]),
  );

  const gender = transformGender(
    getParameterValueByKeySet(urlParameters, [
      'gender',
      'gender-applicant',
      'applicant[applicant][gender]',
      'gnd1',
    ]),
  );

  const dateOfBirth = transformDob(
    getParameterValueByKeySet(urlParameters, [
      'dob',
      'dateofbirth',
      'date_of_birth',
      'dob1',
      'dob-applicant',
      'applicant[applicant][dateofbirth]',
    ]) || '',
  );

  const firstName = getParameterValueByKeySet(urlParameters, [
    'fname',
    'firstname',
    'fn1',
    'first_name',
    'firstname-applicant',
  ]);

  const lastName = getParameterValueByKeySet(urlParameters, [
    'lname',
    'lastname',
    'ln1',
    'last_name',
    'lastname-applicant',
  ]);

  return tobaccoUse || gender || dateOfBirth || firstName || lastName
    ? (removeEmptyKeys({
        tobaccoUse,
        memberType: MemberType.Enum.PRIMARY,
        gender,
        dateOfBirth,
        firstName,
        lastName,
      }) as Applicant)
    : undefined;
}

export async function getParameters({
  baseUrl,
  landingUrl,
}: {
  baseUrl: string;
  landingUrl: string;
}): HandleContextualTrafficGetParametersResult {
  let urlParameters = getSafeURLParameters(landingUrl, true);

  const dataParam = getDataParam(landingUrl);
  if (urlParameters.utm_source === 'mediaalpha' && dataParam) {
    try {
      const response = await decryptParamsService(baseUrl, {
        data: dataParam,
      });
      if (response.status === 200 && response.body?.data) {
        urlParameters = getSafeURLParameters(
          `${landingUrl}&${response.body.data}`,
        );
      }
    } catch (error) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToHandleContextualTraffic',
            rootCause: error as Error,
          },
        ],
      });
    }
  }

  const children: Array<Child> = [];

  for (let index = 1; index < 5; index += 1) {
    const child = getMember(
      urlParameters,
      index.toString(),
      MemberType.Enum.CHILD,
    ) as Child;
    if (child) {
      children.push(child);
    }
  }

  const applicant = getApplicant(urlParameters);
  const contactInformation = {
    email: getParameterValueByKeySet(urlParameters, [
      'e',
      'email',
      'email_address',
    ]),
    address: getParameterValueByKeySet(urlParameters, ['address']),
    phoneNumber: getParameterValueByKeySet(urlParameters, ['phone']),
    firstName: applicant?.firstName,
    lastName: applicant?.lastName,
  };

  const temp = removeEmptyKeys({
    householdSize: transformHouseholdSize(
      getParameterValueByKeySet(urlParameters, [
        'size',
        'householdsize',
        'applicant[householdsize]',
      ]),
    ) as HouseholdSize,
    householdIncome: transformHouseholdIncome(
      getParameterValueByKeySet(urlParameters, [
        'income',
        'householdincome',
        'applicant[householdincome]',
      ]),
    ),

    applicant,
    spouse: getMember(urlParameters, 's', MemberType.Enum.SPOUSE) as Spouse,
  });

  const householdSize =
    (temp.householdSize as number) ??
    children.length + (temp.applicant ? 1 : 0) + (temp.spouse ? 1 : 0);

  const prospect: Prospect = {
    ...temp,
    children,
    contactInformation: removeEmptyKeys(contactInformation),
    householdSize,
    sold: false,
  };

  const prospectResult = Prospect.safeParse(prospect);

  if (!prospectResult.success) {
    return createErrorOperationResult({
      errors: [
        {
          code: 'failedToHandleContextualTraffic',
          rootCause: prospectResult.error,
        },
      ],
    });
  }

  return createSuccessOperationResult(prospectResult.data);
}
